import React from "react";
import { addEnum, Field } from "typegraph";

import CountResult from "common/entities/CountResult";
import { FlexJustify } from "common/fragments/Flex";
import { IPageableListQuery } from "common/models/IPageableListQuery";
import { BlockVariant, BlockView } from "common/views/block/BlockView.view";
import { CardVariant, CardView } from "common/views/card/Card.view";
import { OptionDropdownSelect } from "common/views/dropdownSelect/OptionDropdownSelect";
import PagingView from "common/views/paging/Paging.view";
import Query from "core/api/QueryComponent";
import Label from "core/components/label/Label";
import { IPanelProps, Panel } from "core/decorators/Panel";
import IdName from "core/util/IdName";
import StringUtil from "core/util/StringUtil";
import { iDef } from "core/util/TypeUtils";
import { RowsView } from "../../common/views/rows/Rows.view";
import { FragmentLayout } from "../../core/components/FragmentLayout";
import labelProvider from './Certificates.labels';
import { CertificatesPanelConfig } from "./CertificatesConfig";
import * as fragments from "./fragments";

const statuses = {
	Active: addEnum('Active'),
	Inactive: addEnum('Inactive'),
	Expired: addEnum('Expired'),
	Deleted: addEnum('Deleted'),
	Candidate: addEnum('Candidate'),
	Finished: addEnum('Finished'),
	Suspended: addEnum('Suspended')
};

class CreditEntry {
	@Field()
	public id: string;

	@Field()
	public granted: boolean;

}

class Subscription {
	@Field({entity: CreditEntry})
	public credits?: null | CreditEntry[];
}
class IssuedCertificate {

	@Field()
	public id: number;

	@Field()
	public guid: string;

	@Field()
	public issueDate: string;

	@Field()
	public expiryDate: string;

	@Field()
	public itemName: string;

	@Field({ entity: IdName })
	public certificate: IdName;
	
	@Field({ entity: Subscription })
	public subscription: Subscription;

}

class CertificateQuery implements IPageableListQuery<IssuedCertificate> {

	@Field({
		entity: IssuedCertificate,
		params: (params) => ({params}),
		aliasFor: "issuedCertificatesUser"
	})
	public entities: null | IssuedCertificate[];

	@Field({
		entity: CountResult,
		params: (params) => ({params}),
		aliasFor: "issuedCertificatesUserCount"
	})
	public count: CountResult;

}

@Panel({ id: "certificates", configClass: CertificatesPanelConfig, labelProvider, pageParams: true })
class CertificatesPanel extends React.PureComponent<IPanelProps<CertificatesPanelConfig>> {

	public render() {
		const { config, pageParams } = this.props;

		const params = Object.entries(config.certificateParams || {}).reduce((a, [k, v]) => {

			if (k === 'statuses') {
				const clist = [];
				if (Array.isArray(v)) {
					for (const c of v) {
						if (statuses[c]) {
							clist.push(statuses[c]);
						}
					}
				} else {
					if (statuses[v]) {
						clist.push(statuses[v]);
					}
				}
				if (clist.length > 0) {
					a[k] = clist;
				}
			} else {
				a[k] = v;
			}

			return a;
		}, {});

		const defaultPageSize = iDef(config.pageSize) ? config.pageSize : 10;
		const currentPageSize: number = StringUtil.toInteger(pageParams['pageSize'], defaultPageSize);

		let pageNumber = StringUtil.toInteger(pageParams['pageNumber'], 0);
		if (pageNumber < 1) {
			pageNumber = 0;
		}

		params['pageSize'] = currentPageSize;
		params['pageNumber'] = pageNumber;

		let sortStr: string = pageParams['sort'];
		let sortDesc = false;
		if (sortStr && sortStr.startsWith('-')) {
			sortStr = sortStr.slice(1);
			sortDesc = true;
		}
		if (sortStr && ['issueDate' /* add other valid sorts here */].indexOf(sortStr) > -1) {
			params['sort'] = sortStr;
			if (sortDesc) {
				params['sort'] = '-' + params['sort'];
			}
		} else {
			params['sort'] = '-issueDate';
		}

		return [
			<Query.Root entity={CertificateQuery} args={params}>
				{
					({data}) => (
						<React.Fragment>
							<BlockView viewKey="refine">
								<FlexJustify>
									<div />
									<OptionDropdownSelect
										viewKey="sort"
										value={params['sort']}
										onChange={this.onChangeSort}
										options={[
											{
												value: '-issueDate',
												label: <Label k="sort.issueDate.desc" v="Most Recent" />
											},
											{
												value: 'issueDate',
												label: <Label k="sort.issueDate" v="Oldest" />
											}
										]}
									>
										<Label k="sort" v="Sort" />
									</OptionDropdownSelect>
								</FlexJustify>
							</BlockView>
							<RowsView
								items={data && data.entities || []}
								renderItem={this.renderCertificate}
								viewKey="certificates.list"
							/>
							<BlockView viewKey="paging.block">
								<PagingView
									viewKey="paging"
									pageNumber={pageNumber}
									pageSize={currentPageSize}
									total={data && data.count.count || 0}
									setPageNumber={(n) => this.props.actions.pushPageParam('pageNumber', n.toString())}
								/>
							</BlockView>
						</React.Fragment>
					)
				}
			</Query.Root>
		];
	}

	private onChangeSort = (sort: string) => this.props.actions.pushPageParam('sort', sort);

	public renderCertificate = (cert: IssuedCertificate) => {
		const { config } = this.props;

		return (
			<CardView key={(cert && cert.id) || "skeleton"} variant={CardVariant.SURFACE} viewKey="card">
				<BlockView variant={BlockVariant.CARD_CONTENT} viewKey="card-content">
					<FragmentLayout
						layout={config.itemLayout}
						fragments={fragments}
						props={cert}
					/>
				</BlockView>
			</CardView>
		);
	}
}



// WEBPACK FOOTER //
// ./src/panels/certificates/Certificates.panel.tsx